import React from 'react';
import { DataAPIContext, GET_ONE } from '../../api';

// CP-8331: a booking's invitation link (`branchio_link`) is not part of the create response.
// The backend mints it in a background task it schedules a few seconds after the booking is
// saved (Tour_UserViewset.create: countdown=3, then a Branch.io call), so the row the grid
// shows right after "Add" has no link yet. A guide added through a "Guides" tag category
// looked different only because that path schedules the task without the delay.
//
// watch(id) re-reads the booking until its link exists, then calls onReady(id, link) so the
// grid can refresh: the link shows up on its own, with no page reload. The check is bounded --
// a booking whose link never comes (a tenant with outbound messages blocked, a failed task)
// just stops being watched.
export const LINK_WATCH_INTERVAL_MS = 2000;
export const LINK_WATCH_MAX_CHECKS = 15;

export const useInvitationLinkWatch = ({ resource, onReady }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const timers = React.useRef(new Map());
    const unmounted = React.useRef(false);
    const onReadyRef = React.useRef(onReady);
    onReadyRef.current = onReady;

    React.useEffect(() => {
        const pending = timers.current;
        return () => {
            unmounted.current = true;
            pending.forEach(clearTimeout);
            pending.clear();
        };
    }, []);

    const watch = React.useCallback((id, { maxChecks = LINK_WATCH_MAX_CHECKS } = {}) => {
        if (!id || unmounted.current || timers.current.has(id)) {
            return;
        }
        let checks = 0;
        const check = async () => {
            checks += 1;
            let link = null;
            let gone = false;
            try {
                const res = await dataAPI(GET_ONE, resource, { id });
                if (res?.statusCode === 200) {
                    link = res.data?.branchio_link || null;
                } else if (res?.statusCode === 404) {
                    gone = true;
                }
            } catch (e) {
                // A failed read counts as a check; the next one tries again.
            }
            if (unmounted.current) {
                return;
            }
            if (link || gone || checks >= maxChecks) {
                timers.current.delete(id);
                if (link) {
                    onReadyRef.current?.(id, link);
                }
                return;
            }
            timers.current.set(id, setTimeout(check, LINK_WATCH_INTERVAL_MS));
        };
        timers.current.set(id, setTimeout(check, LINK_WATCH_INTERVAL_MS));
    }, [dataAPI, resource]);

    const isWatching = React.useCallback((id) => timers.current.has(id), []);

    return { watch, isWatching };
};

export default useInvitationLinkWatch;
